import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Select } from '@arco-design/web-react';

import { isFieldDisabled, isFieldReadonly } from '../../platform/capability-registry/fieldControl';
import { isFieldVisible } from '../../platform/capability-registry/resolveFieldMode';
import { shouldSkipCapabilityFetch } from '../../platform/capability-registry/shouldSkipCapabilityFetch';
import { buildOptionFromSelect, useSyncOptionMetaField } from '../../platform/option-meta';
import { getRunPolicyTypes } from './api';
import { getRunPolicyDescription } from './constants';
import { RUN_POLICY_FIELD_KEYS } from './fieldKeys';
import styles from './index.module.less';
import type { RunPolicyBlockProps, RunPolicyPaths } from './types';

const FormItem = Form.Item;

/** 运行策略能力块（对齐云主机 / 容器 / 下单 run-policy） */
export function RunPolicyBlock(props: RunPolicyBlockProps) {
  const { form, paths: rawPaths, options, ...control } = props;
  const paths = rawPaths as RunPolicyPaths;
  const { t } = useTranslation();

  const staticOptions = options?.options;
  const loadFromApi = options?.loadFromApi !== false;
  const fixedValue = options?.fixedValue;
  const showPolicyDescription = options?.showPolicyDescription !== false;
  const labelVariant = options?.labelVariant ?? 'runningPolicy';

  const [apiOptions, setApiOptions] = useState<string[]>([]);

  const label = labelVariant === 'restartPolicy' ? t('cluster:restart-policy') : t('common:running.policy');

  const placeholder =
    labelVariant === 'restartPolicy'
      ? `${t('cluster:select-placeholder')}${t('cluster:restart-policy')}`
      : t('common:please.select.the.operation.policy');

  const requiredMessage =
    labelVariant === 'restartPolicy'
      ? t('cluster:required', { content: t('cluster:restart-policy') })
      : t('common:please.select.the.operation.policy');

  const skipListFetch = shouldSkipCapabilityFetch(control, {
    skipFetch: options?.skipFetch,
    fieldKeys: [RUN_POLICY_FIELD_KEYS.strategy],
  });

  useEffect(() => {
    if (fixedValue) {
      form.setFieldValue(paths.strategy, fixedValue);
      return;
    }
    if (staticOptions?.length) {
      return;
    }
    if (!loadFromApi || skipListFetch) {
      return;
    }

    let cancelled = false;
    void getRunPolicyTypes()
      .then((res) => {
        if (cancelled) return;
        setApiOptions(Array.isArray(res?.runStrategy) ? res.runStrategy : []);
      })
      .catch((error) => {
        console.log(error);
        if (!cancelled) setApiOptions([]);
      });

    return () => {
      cancelled = true;
    };
  }, [fixedValue, form, loadFromApi, paths.strategy, skipListFetch, staticOptions]);

  const policyOptions = useMemo(() => {
    if (fixedValue) return [fixedValue];
    if (staticOptions?.length) return staticOptions;
    return apiOptions;
  }, [apiOptions, fixedValue, staticOptions]);

  const policyListItems = useMemo(() => policyOptions.map((value) => ({ value, label: value })), [policyOptions]);

  useSyncOptionMetaField(form, {
    field: paths.strategy,
    listItems: policyListItems,
    buildOption: (val) => {
      const text = String(val ?? '');
      if (!text) return undefined;
      const desc = getRunPolicyDescription(text);
      const label = desc ? `${text} ${desc}` : text;
      return buildOptionFromSelect(text, { label, name: label });
    },
  });

  const readonly = isFieldReadonly(RUN_POLICY_FIELD_KEYS.strategy, control);
  const disabled = isFieldDisabled(RUN_POLICY_FIELD_KEYS.strategy, control) || !!fixedValue;

  const rules = useMemo(() => (readonly ? undefined : [{ required: true, message: requiredMessage }]), [readonly, requiredMessage]);

  if (!isFieldVisible(RUN_POLICY_FIELD_KEYS.strategy, control)) {
    return null;
  }

  return (
    <>
      <FormItem label={label} field={paths.strategy} rules={rules} disabled={disabled}>
        <Select showSearch placeholder={placeholder} disabled={readonly || disabled}>
          {policyOptions.map((value) => (
            <Select.Option key={value} value={value}>
              <span>{value}</span>
              {showPolicyDescription && getRunPolicyDescription(value) ? (
                <span className={styles.optionDesc}>{getRunPolicyDescription(value)}</span>
              ) : null}
            </Select.Option>
          ))}
        </Select>
      </FormItem>
    </>
  );
}
